import styled from 'styled-components';
import React from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { sessionLeave, sessionService } from '../../services/sessionService';
import { setError } from '../../store/app/actions';
import {
  queueEmail as queueEmailAction,
  readyToBook,
  sessionId as sessionIdAction,
  setIsExistUserInDB,
} from '../../store/queue/actions';
import { Button } from '../../components/UI';

const HIGHLIGHT_COLOR = '#ffbf00';
const BODY_TEXT_COLOR = '#9e9e9e';

export const Queue = ({
  position,
  sessionId,
  queueEmail,
}: {
  position: number;
  sessionId: string | null;
  queueEmail: string | null;
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const handleLeaveQueue = async () => {
    try {
      await sessionLeave(sessionId, queueEmail);
    } catch (e) {
      dispatch(setError({ message: 'Что то пошло не так((', type: 'error' }));
      console.error(e);
    } finally {
      sessionService.clearSession();
      dispatch(queueEmailAction(null));
      dispatch(sessionIdAction(null));
      dispatch(readyToBook(false));
      dispatch(setIsExistUserInDB(false));
      history.replace('/');
    }
  };

  return (
    <WaitingRoomContainer>
      <Section>
        <Title>Online Waiting Room</Title>
        <WarningBlock>
          <WarningText>Don&apos;t refresh this page.</WarningText>
          <WarningText>Don&apos;t use another browser or device.</WarningText>
          <WarningTextLarge>It will put you at the end of the queue.</WarningTextLarge>
        </WarningBlock>
        <BodyTextWhite>You&apos;re already in line!</BodyTextWhite>
        <BodyText>You&apos;ll be automatically directed to the site when it&apos;s your turn.</BodyText>
        <PositionLine>
          <PositionLabel>Your position in the queue: </PositionLabel>
          <PositionNumber>{position}</PositionNumber>
        </PositionLine>
        <BodyText>Please wait while we process your request...</BodyText>
      </Section>

      <Divider />

      <Section>
        <Title>Виртуальная Комната Ожидания</Title>
        <WarningBlock>
          <WarningText>Не обновляй эту страницу.</WarningText>
          <WarningText>Не используй другие браузеры или устройства.</WarningText>
          <WarningTextLarge>Это поместит тебя в конец очереди.</WarningTextLarge>
        </WarningBlock>
        <BodyTextWhite>Ты уже в комнате ожидания!</BodyTextWhite>
        <BodyText>Ты автоматически попадешь на сайт, когда подойдет твоя очередь.</BodyText>
        <PositionLine>
          <PositionLabel>Твой номер в очереди: </PositionLabel>
          <PositionNumber>{position}</PositionNumber>
        </PositionLine>
        <BodyText>Ожидай, пока мы обрабатываем твой запрос...</BodyText>
      </Section>

      <ButtonWrapper>
        <Button onClick={handleLeaveQueue}>Leave Queue</Button>
      </ButtonWrapper>
    </WaitingRoomContainer>
  );
};

const WaitingRoomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  text-align: center;
  background-color: ${(props) => props.theme.colors.bg};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1rem;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1.5rem;
  font-family: ${(props) => props.theme.fonts.regularFont};
  font-weight: bold;
`;

const TEXT_SIZE = '0.9375rem';
const WARNING_TEXT_SIZE = '1.125rem';
const WARNING_TEXT_LARGE_SIZE = '1.125rem';
const BLOCK_SPACING = '1.25rem';
const POSITION_LINE_COLOR = '#ffffff';

const WarningBlock = styled.div`
  margin-bottom: ${BLOCK_SPACING};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WarningText = styled.span`
  font-size: ${WARNING_TEXT_SIZE};
  color: ${HIGHLIGHT_COLOR};
  font-weight: bold;
  margin-bottom: 0.35rem;
  font-family: ${(props) => props.theme.fonts.regularFont};

  &:last-child {
    margin-bottom: 0;
  }
`;

const WarningTextLarge = styled(WarningText)`
  font-size: ${WARNING_TEXT_LARGE_SIZE};
`;

const HighlightText = styled.span`
  font-size: ${TEXT_SIZE};
  color: ${HIGHLIGHT_COLOR};
  font-weight: bold;
  margin-bottom: 0.35rem;
  font-family: ${(props) => props.theme.fonts.regularFont};
`;

const BodyText = styled.div`
  font-size: ${TEXT_SIZE};
  color: ${BODY_TEXT_COLOR};
  margin-bottom: ${BLOCK_SPACING};
  font-family: ${(props) => props.theme.fonts.regularFont};
`;

const BodyTextWhite = styled(BodyText)`
  color: #ffffff;
`;

const PositionLine = styled.div`
  font-size: ${WARNING_TEXT_SIZE};
  font-weight: bold;
  margin-bottom: 0.35rem;
  font-family: ${(props) => props.theme.fonts.regularFont};
`;

const PositionLabel = styled.span`
  color: ${HIGHLIGHT_COLOR};
`;

const PositionNumber = styled.span`
  color: ${POSITION_LINE_COLOR};
`;

const Divider = styled.hr`
  width: 100%;
  max-width: 600px;
  border: none;
  border-top: 1px solid ${HIGHLIGHT_COLOR};
  margin: 1.5rem 0;
`;

const ButtonWrapper = styled.div`
  margin-top: 2rem;
`;
